import {
  InfoBanner,
  InfoIcon,
  SquareNewWindowIcon,
  Tooltip,
  TooltipContent,
  Typography,
} from "@codat/orchard-ui";
import { useProducts } from "contexts/ProductsContext";
import React, { useState } from "react";

import Button from "components/Button";
import Modal from "components/Modal";
import ModalBody from "components/ModalBody";

import styles from "./ProductModal.module.css";

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  productName: string;
  productHeader: string;
  hideAssessSyncForCommerceWarning: boolean;
  learnMoreUrl: string;
  productIcon: React.ReactElement;
  eventProperties?: Record<string, unknown>;
  onEnable: () => void;
}

const ProductModal = ({
  isOpen,
  onClose,
  isAdmin,
  productName,
  productHeader,
  hideAssessSyncForCommerceWarning,
  learnMoreUrl,
  productIcon,
  eventProperties,
  onEnable,
}: ProductModalProps) => {
  const [showNoPermissionsTooltip, setShowNoPermissionsTooltip] =
    useState(false);

  const {
    productsEnabledStatus: { isSyncForCommerceEnabled, isLendingEnabled },
  } = useProducts();
  const showMultipleProductsWarning =
    !hideAssessSyncForCommerceWarning &&
    (isLendingEnabled || isSyncForCommerceEnabled);

  return (
    <>
      <Modal eventProperties={eventProperties} onClose={onClose}>
        {isOpen && (
          <ModalBody
            className={styles.modal}
            eventProperties={eventProperties}
            size="large"
            data-testid="sync-for-commerce-modal"
            title={
              <Typography
                as="h1"
                variant="heading_xl"
                className={styles.title}
                data-testid="modal-title"
              >
                {productHeader}
              </Typography>
            }
            overline={
              <div className={styles.overline} data-testid="modal-overline">
                {productIcon}
                <Typography as="span" variant="heading_sm">
                  {productName}
                </Typography>
              </div>
            }
            onClose={onClose}
          >
            <div className={styles.termsWrapper}>
              <Typography variant="body_sm" as="small" className={styles.terms}>
                {`${productName} is free during your Free Trial, and charged
                at the rate of your plan thereafter.`}
              </Typography>
            </div>
            {showMultipleProductsWarning && (
              <InfoBanner
                color="neutral"
                icon={<InfoIcon />}
                title={
                  <Typography as="p" variant="body_sm">
                    We do not recommend having{" "}
                    <Typography as="span" variant="heading_xs">
                      Sync For Commerce
                    </Typography>{" "}
                    and{" "}
                    <Typography as="span" variant="heading_xs">
                      Assess
                    </Typography>{" "}
                    enabled at the same time.
                  </Typography>
                }
                className={styles.warning}
              />
            )}
            <div className={styles.footer}>
              <Tooltip
                className={styles.noPermissionTooltip}
                id="no-enable-permissions-tooltip"
                isOpen={!isAdmin && showNoPermissionsTooltip}
                placement="top"
                content={
                  <TooltipContent
                    title={
                      <Typography variant="heading_sm" as="p">
                        {`You don't have permission for this`}
                      </Typography>
                    }
                  >
                    <Typography variant="body_sm" as="p">
                      Please ask an administrator to enable this product
                    </Typography>
                  </TooltipContent>
                }
              >
                <Button
                  label="Enable"
                  buttonName="Enable"
                  aria-disabled={!isAdmin}
                  onClick={isAdmin ? onEnable : undefined}
                  onMouseEnter={() => {
                    setShowNoPermissionsTooltip(true);
                  }}
                  onFocus={() => {
                    setShowNoPermissionsTooltip(true);
                  }}
                  onMouseLeave={() => setShowNoPermissionsTooltip(false)}
                />
              </Tooltip>
              <Button
                buttonName="Learn More"
                href={learnMoreUrl}
                target="_blank"
                rel="noopener"
                variant="secondary"
                color="interface"
                id="learn-more-btn"
                data-autofocus
              >
                <span>Learn more</span>
                <SquareNewWindowIcon />
              </Button>
            </div>
          </ModalBody>
        )}
      </Modal>
    </>
  );
};

export default ProductModal;
